import { useMemo, type ReactNode } from "react";

import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { useLoadNext } from "app/hooks";

import { GroupedUsersGrid } from "./GroupedUsersGrid";
import { groupingDetailPath, type RetainedSearch } from "./userGrouping";

import type { GroupUsersGrouping_query$key } from "./__generated__/GroupUsersGrouping_query.graphql";
import type { GroupUsersGroupingQuery } from "./__generated__/GroupUsersGroupingQuery.graphql";
import type { GroupUsersGroupingRefetchQuery } from "./__generated__/GroupUsersGroupingRefetchQuery.graphql";
import type { BucketRow } from "./GroupedUsersGrid";

/** Groups loaded per page as the bucket list scrolls. */
const GROUP_PAGE = 25;

interface Props extends RetainedSearch {
  /** The group-by dropdown, for the grid toolbar. */
  toolbarActions?: ReactNode;
}

/**
 * Lists user groups, loading more as the list scrolls; expanding one shows its
 * members.
 */
export function GroupUsersGrouping({
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
}: Props) {
  const queryData = useLazyLoadQuery<GroupUsersGroupingQuery>(
    graphql`
      query GroupUsersGroupingQuery($first: Int) {
        ...GroupUsersGrouping_query @arguments(first: $first)
      }
    `,
    { first: GROUP_PAGE },
  );

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    GroupUsersGroupingRefetchQuery,
    GroupUsersGrouping_query$key
  >(
    graphql`
      fragment GroupUsersGrouping_query on Query
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" })
      @refetchable(queryName: "GroupUsersGroupingRefetchQuery") {
        userGroups(first: $first, after: $after)
          @connection(key: "GroupUsersGrouping_userGroups") {
          edges {
            node {
              displayName
              id
              name
              source {
                idpAlias
                idpNamespace
              }
              uuid
            }
          }
          pageInfo {
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows = useMemo<BucketRow[]>(
    () =>
      (data.userGroups.edges ?? [])
        .map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => {
          const label = node.displayName || node.name;
          return {
            id: node.id,
            label,
            to: groupingDetailPath({
              kind: "group",
              uuid: node.uuid,
              name: label,
            }),
            editTo: `/settings/access-control/groups/${node.uuid}/edit`,
            memberScope: { kind: "group-uuid" as const, groupUuid: node.uuid },
            highlightGroupUuid: node.uuid,
            detail: node.source
              ? `Synced from ${node.source.idpAlias || node.source.idpNamespace}`
              : "Local",
          };
        }),
    [data.userGroups.edges],
  );

  const loadMore = useLoadNext(hasNext, loadNext, GROUP_PAGE);

  return (
    <GroupedUsersGrid
      bucketTotal={data.userGroups.pageInfo.total ?? null}
      buckets={rows}
      initialSearchTerm={initialSearchTerm}
      loading={isLoadingNext}
      onLoadMore={loadMore}
      onSearchTermChange={onSearchTermChange}
      searchKind="group"
      tableId="grouped-users-groups"
      toolbarActions={toolbarActions}
      unit="group"
    />
  );
}
